import formatAmount from 'core/helpers/formatAmount'

import { syntax, syntaxDark } from 'core/styles'
import type { StaticFieldSchema } from 'core/utils/generated/formSchemas'

export type ColorType =
  | 'boolean'
  | 'string'
  | 'number'
  | 'currency'
  | 'array'
  | 'object'
  | 'null'
  | 'emptyString'
  | 'emptyArray'
  | 'emptyObject'
  | 'unknown'

// Shares its palette with JsonView via theme.syntax, so a value renders the
// same color whether it's read from the Form or JSON tab.
const COLOR_TYPE_TO_SYNTAX_KEY: { [T in ColorType]: keyof typeof syntax } = {
  boolean: 'boolean',
  string: 'string',
  number: 'number',
  currency: 'currency',
  array: 'container',
  object: 'container',
  unknown: 'container',
  null: 'null',
  emptyString: 'muted',
  emptyArray: 'muted',
  emptyObject: 'muted',
}

export const getFieldColorType = (
  componentType: string,
  value: unknown,
  schemaField?: StaticFieldSchema,
): ColorType => {
  if (value === null || value === undefined) return 'null'

  if (Array.isArray(value)) {
    return value.length === 0 ? 'emptyArray' : 'array'
  }
  if (typeof value === 'object') {
    return Object.keys(value as { [key: string]: unknown }).length === 0
      ? 'emptyObject'
      : 'object'
  }
  if (typeof value === 'string' && value.trim() === '') {
    return 'emptyString'
  }

  if (schemaField) {
    if (
      schemaField.type === 'number' &&
      schemaField.numberFormat === 'currency'
    ) {
      const fieldPath = schemaField.fullPath || ''
      const isCurrencyField =
        fieldPath.toLowerCase().includes('amount') ||
        fieldPath.toLowerCase().includes('limit') ||
        fieldPath.toLowerCase().includes('fee') ||
        fieldPath.toLowerCase().includes('cost') ||
        fieldPath.toLowerCase().includes('price') ||
        fieldPath.toLowerCase().includes('balance')

      if (isCurrencyField) {
        return 'currency'
      }
      return 'number'
    }

    if (schemaField.type === 'string' && typeof value === 'boolean') {
      return 'boolean'
    }
    if (schemaField.type === 'string' && typeof value === 'number') {
      return 'number'
    }

    switch (schemaField.type) {
      case 'boolean':
        return 'boolean'
      case 'number':
        return 'number'
      case 'string':
      case 'enum':
        return 'string'
      case 'array':
        return 'array'
      case 'object':
        return 'object'
      default:
        return 'unknown'
    }
  }

  switch (componentType) {
    case 'Checkbox':
      return 'boolean'
    case 'NumberInput':
      return 'number'
    default:
      return 'string'
  }
}

/** Color type from the runtime value alone, for renderers without schema access
 * (e.g. items inside ArrayValueView). */
export const getValueColorType = (value: unknown): ColorType => {
  if (value === null || value === undefined) return 'null'
  if (Array.isArray(value)) {
    return value.length === 0 ? 'emptyArray' : 'array'
  }
  if (typeof value === 'object') {
    return Object.keys(value as { [key: string]: unknown }).length === 0
      ? 'emptyObject'
      : 'object'
  }
  if (typeof value === 'string') {
    return value.trim() === '' ? 'emptyString' : 'string'
  }
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return 'number'
  return 'unknown'
}

export const getViewModeColor = (
  colorType: ColorType,
  darkMode: boolean,
): string =>
  (darkMode ? syntaxDark : syntax)[COLOR_TYPE_TO_SYNTAX_KEY[colorType]]

export const formatViewValue = (
  value: unknown,
  colorType: ColorType,
): string => {
  if (colorType === 'null') return 'null'
  if (colorType === 'emptyObject') return '{}'
  if (colorType === 'emptyArray') return '[]'
  if (colorType === 'emptyString') return "''"

  if (Array.isArray(value)) {
    // Arrays of primitives
    const allPrimitives = value.every(
      (v) => v === null || v === undefined || typeof v !== 'object',
    )
    if (allPrimitives) {
      return value.map((v) => String(v)).join(', ')
    }

    // Arrays of arrays
    const allArrays = value.every((v) => Array.isArray(v))
    if (allArrays) {
      const rendered = (value as Array<Array<unknown>>)
        .map((arr) => `[${arr.map((x) => String(x)).join(', ')}]`)
        .join('; ')
      return `[${rendered}]`
    }

    // Arrays of objects (or mixed)
    const items = (value as Array<unknown>).map((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const obj = item as { [key: string]: unknown }
        // Special case: common display for name/email objects
        const hasNameOrEmail = 'name' in obj || 'email' in obj
        if (hasNameOrEmail) {
          const name = obj.name ? String(obj.name) : ''
          const email = obj.email ? String(obj.email) : undefined
          return email ? `${name ? name + ' ' : ''}<${email}>` : name || '{}'
        }

        const entries = Object.entries(obj)
          .map(([k, v]) => `${k}: ${renderPrimitive(v)}`)
          .join(', ')
        return `{ ${entries} }`
      }
      if (Array.isArray(item)) {
        return `[${item.map((x) => String(x)).join(', ')}]`
      }
      return String(item)
    })

    return `[${items.join('; ')}]`
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value)
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }
  if (colorType === 'currency' && typeof value === 'number') {
    return formatAmount(value)
  }
  return String(value)
}

const renderPrimitive = (v: unknown): string => {
  if (v === null) return 'null'
  if (v === undefined) return 'undefined'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (Array.isArray(v)) return `[${v.map((x) => String(x)).join(', ')}]`
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}
